import React, { useState, useEffect } from 'react';
import { Search, Users, Brain, Wifi, Star, FileText, Server, Info, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { stemmer } from 'stemmer';

const EnhancedAgents = () => {
  const navigate = useNavigate();
  const [allAgents, setAllAgents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [stats, setStats] = useState({
    total: 0,
    skill: 0,
    persona: 0,
    communication: 0,
    mcp: 0
  });
  const [pagination, setPagination] = useState({
    all: { page: 1, hasMore: true, total: 0 },
    skill: { page: 1, hasMore: true, total: 0 },
    persona: { page: 1, hasMore: true, total: 0 },
    communication: { page: 1, hasMore: true, total: 0 },
    mcp: { page: 1, hasMore: true, total: 0 }
  });
  const [error, setError] = useState(null);
  const [showTooltip, setShowTooltip] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Tooltip component
  const InfoTooltip = ({ id, text }) => (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onMouseEnter={() => setShowTooltip(id)}
        onMouseLeave={() => setShowTooltip(null)}
        style={{
          background: 'transparent',
          border: 'none',
          borderRadius: '50%',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'help',
          marginLeft: '8px',
          padding: '0',
          outline: 'none',
          transition: 'all 0.2s ease',
          opacity: showTooltip === id ? 1 : 0.5
        }}
      >
        <Info size={12} style={{ color: 'var(--text-secondary)' }} />
      </button>
      {showTooltip === id && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: '50%',
          transform: 'translateX(-50%)',
          marginTop: '8px',
          background: 'var(--bg-secondary)',
          border: '1px solid var(--border)',
          borderRadius: '8px',
          padding: '12px 16px',
          minWidth: '250px',
          maxWidth: '350px',
          fontSize: '0.875rem',
          color: 'var(--text-secondary)',
          lineHeight: '1.5',
          zIndex: 1000,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          whiteSpace: 'normal'
        }}>
          {text}
        </div>
      )}
    </div>
  );

  const tabs = [
    { id: 'all', label: 'All Agents', icon: Users, description: 'View all agents across categories' },
    { id: 'persona', label: 'Persona Agents', icon: Star, description: 'Personal and business assistants' },
    { id: 'communication', label: 'Communication Agents', icon: Wifi, description: 'IoT and device communication agents' },
    { id: 'mcp', label: 'MCP Servers', icon: Server, description: 'Model Context Protocol servers and services' }
  ];

  useEffect(() => {
    fetchAgents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Reload agents when tab changes
    fetchAgents();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Search function with debounce
  useEffect(() => {
    const searchAgents = async () => {
      if (!searchTerm || searchTerm.trim().length === 0) {
        setSearching(false);
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const response = await axios.get(`/api/agents/search?q=${encodeURIComponent(searchTerm)}&type=${activeTab}&limit=100`);
        if (response.data.success) {
          setSearchResults(response.data.agents);
          console.log(`Search found ${response.data.agents.length} results for "${searchTerm}"`);
        }
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    };

    // Debounce search - wait 500ms after user stops typing
    const timeoutId = setTimeout(() => {
      searchAgents();
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchTerm, activeTab]);

  const fetchAgents = async (loadMore = false) => {
    try {
      if (!loadMore) {
        setLoading(true);
        setError(null);
        setAllAgents([]);
        setPagination({
          all: { page: 1, hasMore: true, total: 0 },
          skill: { page: 1, hasMore: true, total: 0 },
          persona: { page: 1, hasMore: true, total: 0 },
          communication: { page: 1, hasMore: true, total: 0 },
          mcp: { page: 1, hasMore: true, total: 0 }
        });
      } else {
        setLoadingMore(true);
      }
      
      // Load initial batch of agents for the current tab
      const pageSize = 50;
      const currentPage = loadMore ? pagination[activeTab].page + 1 : 1;
      
      try {
        // Calculate skip based on currently loaded agents for this tab
        const currentLoadedCount = loadMore ? allAgents.length : 0;
        const skip = currentLoadedCount;
        
        console.log(`Loading ${activeTab} agents: page ${currentPage}, skip ${skip}, limit ${pageSize}`);
        
        const response = await axios.get(`/api/agents?type=${activeTab}&limit=${pageSize}&skip=${skip}&t=${Date.now()}`);
        
        let newAgents = response.data.agents || response.data;
        // Set all agents to online status
        newAgents = newAgents.map(agent => ({ ...agent, status: 'online' }));
        const totalFromAPI = response.data.pagination?.total || newAgents.length;
        
        // Calculate the correct total loaded count
        const totalLoaded = loadMore ? currentLoadedCount + newAgents.length : newAgents.length;
        const hasMore = newAgents.length === pageSize && totalLoaded < totalFromAPI;
        
        if (loadMore) {
          // Append to existing agents
          setAllAgents(prev => [...prev, ...newAgents]);
        } else {
          // Replace all agents
          setAllAgents(newAgents);
        }
        
        setPagination(prev => ({
          ...prev,
          [activeTab]: {
            page: currentPage,
            hasMore: hasMore,
            total: totalFromAPI
          }
        }));
        
        console.log(`Loaded ${newAgents.length} ${activeTab} agents (page ${currentPage}, total loaded: ${totalLoaded}/${totalFromAPI}, hasMore: ${hasMore})`);
        
        // Get stats for all categories (only on initial load)
        if (!loadMore) {
          const timestamp = Date.now();
          const [allStats, skillStats, personaStats, commStats, mcpStats] = await Promise.all([
            axios.get(`/api/agents?type=all&limit=1&t=${timestamp}`),
            axios.get(`/api/agents?type=skill&limit=1&t=${timestamp}`),
            axios.get(`/api/agents?type=persona&limit=1&t=${timestamp}`), 
            axios.get(`/api/agents?type=communication&limit=1&t=${timestamp}`),
            axios.get(`/api/agents?type=mcp&limit=1&t=${timestamp}`)
          ]);
          
          const newStats = {
            total: allStats.data.pagination.total,
            skill: skillStats.data.pagination.total,
            persona: personaStats.data.pagination.total,
            communication: commStats.data.pagination.total,
            mcp: mcpStats.data.pagination.total
          };
          
        console.log('Updated stats:', newStats);
        setStats(newStats);
      }
      
    } catch (apiError) {
        console.error('API Error:', apiError.message);
        setError(`Backend API Error: ${apiError.message}`);
        if (!loadMore) {
          setAllAgents([]);
          setStats({ total: 0, skill: 0, persona: 0, communication: 0, mcp: 0 });
        }
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  // normalize and stem search term
  const searchTermNorm = searchTerm.toLowerCase().replace(/[^\w\s]|_/g, '');
  const searchWords = searchTermNorm.split(' ');
  const searchStemmedWords = searchWords.map(word => stemmer(word)); // eslint-disable-line no-unused-vars

  // function to see if any substring in a list of substrings is in a string
  function stringContainsAnySubstring(mainString, substringList) { // eslint-disable-line no-unused-vars
    for (let i = 0; i < substringList.length; i++) {
      if (mainString.includes(substringList[i])) {
        return true; // Found a match
      }
    }
    return false; // No match found
  }

  const getFilteredAgents = () => {
    // If searching, return search results
    if (searchTerm && searchTerm.trim().length > 0) {
      return searchResults;
    }

    // Otherwise return normal filtered agents
    let filtered = allAgents;

    // Filter by tab
    if (activeTab !== 'all') {
      filtered = filtered.filter(agent => agent.category === activeTab);
    }

    return filtered;
  };


  const getAgentCategoryIcon = (category) => {
    switch (category) {
      case 'skill': return <Brain size={16} />;
      case 'persona': return <Star size={16} />;
      case 'communication': return <Wifi size={16} />;
      case 'mcp': return <Server size={16} />;
      default: return <Users size={16} />;
    }
  };

  const getAgentCategoryColor = (category) => {
    // All icons now use red color
    return '#7a2020';
  };

  const getStatusColor = (status) => { // eslint-disable-line no-unused-vars
    switch (status) {
      case 'online': return 'var(--success)';
      case 'verified': return 'var(--success)';
      case 'unverified': return 'var(--warning)';
      case 'offline': return 'var(--error)';
      default: return 'var(--text-secondary)';
    }
  };

  const filteredAgents = getFilteredAgents();

  if (loading) {
    return (
      <div className="page">
        <div className="loading">Loading...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h1 className="page-title" style={{ fontSize: isMobile ? '1.5rem' : undefined }}>NANDA Index</h1>
              {!isMobile && (
                <InfoTooltip 
                  id="page-title"
                  text="This page aggregates agents from multiple MongoDB databases including skill agents, persona agents, communication agents, and MCP servers. Use the tabs to filter by category."
                />
              )}
            </div>
            <p className="page-subtitle" style={{ fontSize: isMobile ? '0.8125rem' : undefined }}>
              Unified view of agents from skill, persona, and communication databases
            </p>
          </div>
          <button
            onClick={() => navigate('/agents/add')}
            className="btn btn-primary"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: isMobile ? '0.8125rem' : '0.9rem',
              padding: isMobile ? '8px 12px' : '10px 16px',
              whiteSpace: 'nowrap'
            }}
          >
            <Plus size={16} />
            Add Agent
          </button>
        </div>
        
        {/* Error/Status Messages */}
        {error && (
          <div style={{
            background: 'rgba(122, 32, 32, 0.1)',
            border: '1px solid rgba(122, 32, 32, 0.3)',
            borderRadius: '8px',
            padding: '12px 16px',
            marginTop: '16px',
            color: '#7a2020',
            fontSize: '0.875rem'
          }}>
            ❌ Error: {error}
          </div>
        )}
      </div>


      {/* Tab Navigation */}
      <div className="tab-container" style={{ marginBottom: isMobile ? '20px' : '30px' }}>
        <div className="tab-nav" style={{ 
          display: 'flex', 
          borderBottom: '2px solid var(--border)',
          marginBottom: isMobile ? '12px' : '20px',
          overflowX: isMobile ? 'auto' : 'visible',
          overflowY: 'visible',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none'
        }}>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const tabLabel = isMobile ? tab.label.replace(' Agents', '').replace(' Servers', '') : tab.label;
            return (
              <button
                key={tab.id}
                className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: isMobile ? '4px' : '8px',
                  padding: isMobile ? '10px 12px' : '12px 20px',
                  border: 'none',
                  background: 'none',
                  color: activeTab === tab.id ? 'var(--accent)' : 'var(--text-secondary)',
                  borderBottom: activeTab === tab.id ? '2px solid var(--accent)' : '2px solid transparent',
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.75rem' : '0.875rem',
                  fontWeight: activeTab === tab.id ? '600' : '400',
                  transition: 'all 0.2s ease',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Icon size={isMobile ? 14 : 16} />
                {tabLabel}
                {tab.id !== 'all' && (
                  <span style={{
                    background: activeTab === tab.id ? 'var(--accent)' : 'var(--bg-tertiary)',
                    color: activeTab === tab.id ? 'white' : 'var(--text-secondary)',
                    padding: isMobile ? '1px 4px' : '2px 6px',
                    borderRadius: '10px',
                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {stats[tab.id]}
                  </span>
                )}
                {!isMobile && (
                  <InfoTooltip 
                    id={`tab-${tab.id}`}
                    text={tab.description}
                  />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Tab Description */}
        <div style={{ 
          color: 'var(--text-secondary)', 
          fontSize: isMobile ? '0.8125rem' : '0.875rem',
          marginBottom: isMobile ? '12px' : '20px'
        }}>
          {tabs.find(tab => tab.id === activeTab)?.description}
        </div>
      </div>

      {/* Search */}
      <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <label style={{ fontSize: isMobile ? '0.8125rem' : '0.875rem', fontWeight: '500', color: 'var(--text-primary)' }}>
            Search Agents
          </label>
          {!isMobile && (
            <InfoTooltip 
              id="search"
              text="Search agents by name, description, or specialties across the entire database. Results appear as you type with a short delay."
            />
          )}
          {searchTerm && !searching && searchResults.length > 0 && (
            <span style={{ 
              color: 'var(--success)', 
              fontSize: '0.8125rem', 
              marginLeft: '12px',
              fontWeight: '500'
            }}>
              {searchResults.length} result{searchResults.length !== 1 ? 's' : ''} found
            </span>
          )}
          {searchTerm && !searching && searchResults.length === 0 && (
            <span style={{ 
              color: 'var(--text-secondary)', 
              fontSize: '0.8125rem', 
              marginLeft: '12px'
            }}>
              No results found
            </span>
          )}
        </div>
        <div style={{ position: 'relative', maxWidth: isMobile ? '100%' : '400px' }}>
          <Search 
            size={isMobile ? 18 : 20}
            style={{ 
              position: 'absolute', 
              left: '12px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'var(--text-secondary)'
            }} 
          />
          <input
            type="text"
            placeholder={searching ? "Searching entire database..." : `Search ${activeTab === 'all' ? 'all' : activeTab} agents across database...`}
            className="input"
            style={{ 
              paddingLeft: isMobile ? '40px' : '44px',
              paddingRight: searching ? '40px' : '12px',
              fontSize: isMobile ? '0.875rem' : undefined
            }}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searching && (
            <div style={{
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              width: '16px',
              height: '16px',
              border: '2px solid rgba(139, 32, 32, 0.3)',
              borderTop: '2px solid #8b2020',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
          )}
        </div>
      </div>

      {/* Agent Grid */}
      <div className={isMobile ? 'grid grid-1' : 'grid grid-3'}>
        {filteredAgents.map((agent) => (
          <div key={agent.id} className="card">
            <div className="card-header" style={{ 
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: isMobile ? 'flex-start' : 'center',
              gap: isMobile ? '12px' : '0'
            }}>
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                  <div style={{ color: getAgentCategoryColor(agent.category) }}>
                    {getAgentCategoryIcon(agent.category)}
                  </div>
                  <h3 className="card-title" style={{ fontSize: isMobile ? '0.9375rem' : undefined }}>{agent.name}</h3>
                </div>
                {/* Agent Username */}
                <div style={{ 
                  fontSize: isMobile ? '0.75rem' : '0.8125rem',
                  color: 'var(--accent)',
                  marginBottom: '6px',
                  fontFamily: 'monospace',
                  fontWeight: '500'
                }}>
                  @{agent.id.replace(/^(skill-|user-|iot-|mcp-)/, '')}
                </div>
                <p style={{ 
                  color: 'var(--text-secondary)', 
                  fontSize: isMobile ? '0.8125rem' : '0.875rem',
                  margin: '4px 0 0 0' 
                }}>
                  {agent.description}
                </p>
              </div>
            </div>

            {/* Agent Info */}
            <div style={{ marginBottom: isMobile ? '12px' : '16px' }}>
              {/* Agent Facts URL - check both facts_url and agentFactsURL */}
              {(agent.factsUrl || agent.facts_url || agent.agentFactsURL) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FileText size={isMobile ? 14 : 16} style={{ color: 'var(--accent)' }} />
                  <a 
                    href={agent.factsUrl || agent.facts_url || agent.agentFactsURL} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      fontSize: isMobile ? '0.8125rem' : '0.875rem',
                      color: 'var(--accent)', 
                      textDecoration: 'none',
                      borderBottom: '1px solid transparent',
                      transition: 'border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.borderBottomColor = 'var(--accent)'}
                    onMouseLeave={(e) => e.target.style.borderBottomColor = 'transparent'}
                  >
                    View AgentFacts →
                  </a>
                </div>
              )}
              
              {/* MCP Server Documentation URL */}
              {agent.category === 'mcp' && agent.documentationUrl && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <FileText size={isMobile ? 14 : 16} style={{ color: '#7a2020' }} />
                  <a 
                    href={agent.documentationUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={{ 
                      fontSize: isMobile ? '0.8125rem' : '0.875rem',
                      color: '#7a2020', 
                      textDecoration: 'none',
                      borderBottom: '1px solid transparent',
                      transition: 'border-color 0.2s ease'
                    }}
                    onMouseEnter={(e) => e.target.style.borderBottomColor = '#8b5cf6'}
                    onMouseLeave={(e) => e.target.style.borderBottomColor = 'transparent'}
                  >
                    View Documentation →
                  </a>
                </div>
              )}
              
              {/* Additional Info for Different Agent Types */}
              {agent.subCategory && agent.category !== 'communication' && agent.category !== 'persona' && (
                <div style={{ fontSize: isMobile ? '0.6875rem' : '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Type: {agent.subCategory}
                </div>
              )}
              {/* MCP Server specific info */}
              {agent.category === 'mcp' && (
                <>
                  {agent.provider && (
                    <div style={{ fontSize: isMobile ? '0.6875rem' : '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Provider: {agent.provider}
                    </div>
                  )}
                  {agent.types && agent.types.length > 0 && (
                    <div style={{ fontSize: isMobile ? '0.6875rem' : '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Types: {agent.types.join(', ')}
                    </div>
                  )}
                  {agent.rating > 0 && (
                    <div style={{ fontSize: isMobile ? '0.6875rem' : '0.75rem', color: 'var(--text-secondary)', marginTop: '4px' }}>
                      Rating: {agent.rating}/5 • Uptime: {agent.uptime}%
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Specialties */}
            {agent.specialties && agent.specialties.length > 0 && agent.category !== 'communication' && agent.category !== 'persona' && (
              // Only show specialties if they're not just repeating the agent name
              (() => {
                const agentNameLower = agent.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                const hasUniqueSpecialties = agent.specialties.some(s => 
                  !s.toLowerCase().replace(/[^a-z0-9]/g, '').includes(agentNameLower) &&
                  !agentNameLower.includes(s.toLowerCase().replace(/[^a-z0-9]/g, ''))
                );
                
                return hasUniqueSpecialties ? (
                  <div style={{ marginBottom: isMobile ? '12px' : '16px' }}>
                    <div style={{ 
                      fontSize: isMobile ? '0.8125rem' : '0.875rem',
                      color: 'var(--text-secondary)', 
                      marginBottom: '8px' 
                    }}>
                      Specialties:
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                      {agent.specialties.slice(0, 3).map((specialty, index) => (
                    <span
                      key={index}
                      style={{
                        background: 'var(--bg-tertiary)',
                        color: 'var(--text-primary)',
                        padding: isMobile ? '3px 6px' : '4px 8px',
                        borderRadius: '6px',
                        fontSize: isMobile ? '0.6875rem' : '0.75rem',
                        border: '1px solid var(--border)'
                      }}
                    >
                      {specialty}
                    </span>
                  ))}
                  {agent.specialties.length > 3 && (
                    <span style={{
                      color: 'var(--text-secondary)',
                      fontSize: isMobile ? '0.6875rem' : '0.75rem',
                      padding: isMobile ? '3px 6px' : '4px 8px'
                    }}>
                      +{agent.specialties.length - 3} more
                    </span>
                  )}
                </div>
              </div>
                ) : null;
              })()
            )}

          </div>
        ))}
      </div>

      {/* Pagination Info and Load More Button */}
      {filteredAgents.length > 0 && (
        <div style={{ 
          marginTop: isMobile ? '20px' : '30px',
          padding: isMobile ? '16px' : '20px',
          background: 'var(--bg-secondary)',
          borderRadius: '8px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '16px'
        }}>
          <div style={{ 
            fontSize: isMobile ? '0.8125rem' : '0.875rem',
            color: 'var(--text-secondary)',
            width: isMobile ? '100%' : 'auto',
            textAlign: isMobile ? 'center' : 'left'
          }}>
            Showing {filteredAgents.length} of {pagination[activeTab]?.total || 0} {activeTab === 'all' ? 'total' : activeTab} agents
            {pagination[activeTab]?.page > 1 && ` (Page ${pagination[activeTab].page})`}
          </div>
          
          {pagination[activeTab]?.hasMore && (
            <button
              onClick={() => fetchAgents(true)}
              disabled={loadingMore}
              style={{
                padding: isMobile ? '12px 20px' : '10px 20px',
                background: loadingMore ? 'var(--bg-tertiary)' : 'var(--accent)',
                color: loadingMore ? 'var(--text-secondary)' : 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: loadingMore ? 'not-allowed' : 'pointer',
                fontSize: isMobile ? '0.8125rem' : '0.875rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease',
                width: isMobile ? '100%' : 'auto'
              }}
              onMouseEnter={(e) => {
                if (!loadingMore) e.target.style.background = 'var(--accent-hover)';
              }}
              onMouseLeave={(e) => {
                if (!loadingMore) e.target.style.background = 'var(--accent)';
              }}
            >
              {loadingMore ? (
                <>
                  <div style={{
                    width: '14px',
                    height: '14px',
                    border: '2px solid var(--text-secondary)',
                    borderTop: '2px solid transparent',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Loading...
                </>
              ) : (
                <>
                  Load More
                  <span style={{ marginLeft: '4px' }}>→</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {filteredAgents.length === 0 && !loading && (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
          {searchTerm ? 
            `No agents found matching "${searchTerm}" in ${activeTab === 'all' ? 'any category' : activeTab + ' agents'}` :
            `No ${activeTab === 'all' ? '' : activeTab + ' '}agents available`
          }
        </div>
      )}
    </div>
  );
};

export default EnhancedAgents;
